import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { UploadProgressItem } from '../models/upload-progress-item';
import { FileService } from './file.service';
import { ChatbotDocumentService } from './chatbot-document.service';
import { CreateUploadRequestDTO } from '../models/chatbot-file-dto';
import { ChatbotDocumentUpdateFileDTO } from '../models/chatbot-document-dto';

type AbortAction = 'pause' | 'cancel';

const STORAGE_KEY = 'sim-chatbot-upload-progress-items';

/** Forma serializable de un ítem para localStorage (File no es serializable). */
interface StoredUploadItem {
  id: string;
  idDocument: string;
  idClient: string;
  idProject: string;
  idChat: string;
  documentTitle: string;
  projectName: string;
  clientName: string;
  chatName: string;
  fileName: string;  
  fileSize: number;
  fileNameGuid: string;
  progress: number;
  completed: boolean;
  error?: boolean;
  IsIa: boolean;
}

const MAX_BLOCK_SIZE_BYTES = 100 * 1024 * 1024;

/** Tamaño de bloque Azure: techo 100 MiB y como máximo el 10% del archivo (bytes, redondeado hacia abajo). */
function blockSizeForFileSize(fileSizeBytes: number): number {
  if (fileSizeBytes <= 0) return 1;
  const tenPercentFloor = Math.floor(fileSizeBytes / 10);
  return Math.min(MAX_BLOCK_SIZE_BYTES, Math.max(1, tenPercentFloor));
}

@Injectable({
  providedIn: 'root'
})
export class UploadProgressService {
  private readonly itemsSubject = new BehaviorSubject<UploadProgressItem[]>([]);
  readonly items$ = this.itemsSubject.asObservable();

  private readonly panelExpandedSubject = new BehaviorSubject<boolean>(false);
  readonly panelExpanded$ = this.panelExpandedSubject.asObservable();

  private fileService = inject(FileService);
  private documentService = inject(ChatbotDocumentService);

  /** Controladores para abortar subidas en curso. */
  private readonly abortControllers = new Map<string, AbortController>();
  /** Acción a aplicar cuando la subida se aborte: pausar (dejar reanudable) o cancelar (quitar). */
  private readonly abortAction = new Map<string, AbortAction>();

  constructor() {
    this.itemsSubject.next(this.loadFromStorage());
  }

  private loadFromStorage(): UploadProgressItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const stored: StoredUploadItem[] = JSON.parse(raw);
      if (!Array.isArray(stored)) return [];
      return stored.map(s => ({
        id: s.id,
        idDocument: s.idDocument,
        idClient: s.idClient,
        idProject: s.idProject,
        idChat: s.idChat,
        documentTitle: s.documentTitle,
        chatName: s.chatName,
        projectName: s.projectName,
        clientName: s.clientName,
        file: { name: s.fileName, size: s.fileSize },
        fileName: s.fileNameGuid,
        progress: s.progress,
        completed: s.completed,
        error: s.error,
        IsIa: s.IsIa
      }));
    } catch {
      return [];
    }
  }

  private saveToStorage(): void {
    const items = this.itemsSubject.value;
    const stored: StoredUploadItem[] = items.map(it => ({
      id: it.id,
      idDocument: it.idDocument,
      idChat: it.idChat,
      idClient: it.idClient,
      idProject: it.idProject,
      documentTitle: it.documentTitle,
      chatName: it.chatName,
      projectName: it.projectName,
      clientName: it.clientName,
      fileName: it.file.name,
      fileNameGuid: it.fileName,
      fileSize: it.file.size,
      progress: it.progress,
      completed: it.completed,
      error: it.error,
      IsIa: it.IsIa
    }));
    try {
      if (stored.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      }
    } catch {
      // ignorar fallos de cuota o localStorage deshabilitado
    }
  }
  
  setPanelExpanded(expanded: boolean): void {
    this.panelExpandedSubject.next(expanded);
  }

  togglePanelExpanded(): void {
    this.panelExpandedSubject.next(!this.panelExpandedSubject.value);
  }

  addItem(item: Omit<UploadProgressItem, 'id' | 'progress' | 'completed'>): string {
    const id = crypto.randomUUID();

    const newItem: UploadProgressItem = {
      ...item,
      id,
      fileName: id + '_' + item.file.name,
      progress: 0,
      completed: false
    };
    const current = this.itemsSubject.value;
    this.itemsSubject.next([...current, newItem]);
    this.saveToStorage();
    this.setPanelExpanded(true);
    this.startUpload(newItem);
    return newItem.id;
  }

  updateProgress(id: string, progress: number): void {
    const current = this.itemsSubject.value;
    const updated = current.map(it =>
      it.id === id
        ? { ...it, progress, completed: progress >= 100 }
        : it
    );
    this.itemsSubject.next(updated);
    this.saveToStorage();
  }

  setError(id: string): void {
    const current = this.itemsSubject.value;
    const updated = current.map(it =>
      it.id === id ? { ...it, error: true, completed: true } : it
    );
    this.itemsSubject.next(updated);
    this.saveToStorage();
  }

  removeCompleted(): void {
    const current = this.itemsSubject.value;
    const filtered = current.filter(it => !it.completed);
    this.itemsSubject.next(filtered);
    this.saveToStorage();
  }

  /** Elimina un ítem de la lista (p. ej. para cancelar una subida pendiente de reanudar). */
  removeItem(id: string): void {
    const current = this.itemsSubject.value;
    const filtered = current.filter(it => it.id !== id);
    this.itemsSubject.next(filtered);
    this.saveToStorage();
  }

  get items(): UploadProgressItem[] {
    return this.itemsSubject.value;
  }

  /** True si la subida se puede reanudar: quedó interrumpida (sin File) o falló por error (p. ej. corte de red). */
  canResume(item: UploadProgressItem): boolean {
    if (item.error) return true;
    return !item.completed && !(item.file instanceof File);
  }

  /**
   * Reanuda una subida interrumpida o fallida. Comprueba que el archivo tenga el mismo nombre y tamaño.
   * @returns { success: true } o { success: false, error: mensaje }
   */
  resumeUpload(itemId: string, file: File): { success: boolean; error?: string } {
    const current = this.itemsSubject.value;
    const item = current.find(it => it.id === itemId);
    console.log("resumeUpload", item);
    if (!item) {
      return { success: false, error: 'Elemento no encontrado.' };
    }
    if (!this.canResume(item)) {
      return { success: false, error: 'Esta subida no se puede reanudar.' };
    }
    if (file.name !== item.file.name || file.size !== item.file.size) {
      return {
        success: false,
        error: 'El archivo debe ser el mismo: mismo nombre y mismo tamaño.'
      };
    }
    const updatedItem: UploadProgressItem = {
      ...item,
      file,
      progress: 0,
      completed: false,
      error: false
    };
    const updated = current.map(it => (it.id === itemId ? updatedItem : it));
    this.itemsSubject.next(updated);
    this.saveToStorage();
    this.startUpload(updatedItem);
    return { success: true };
  }

  startUpload(item: UploadProgressItem): void {
    if (!(item.file instanceof File)) {
      return; // ítem restaurado desde localStorage, no hay archivo real para subir
    }
    const file = item.file;
    const dto: CreateUploadRequestDTO = new CreateUploadRequestDTO();
    dto.IdClient = item.idClient;
    dto.IdProject = item.idProject;
    dto.IdChat = item.idChat;
    dto.FileName = item.fileName;

    const controller = new AbortController();
    this.abortControllers.set(item.id, controller);

    this.fileService.createUploadSas(dto).subscribe(async resp => {
      const urlUpload = resp.Url;
      const blobname = resp.Blobname;

      const blockSize = blockSizeForFileSize(file.size);
      const concurrency = 4;

      try {
        await this.fileService.uploadLargeFileResumable(
          urlUpload,
          blobname,
          file,
          blockSize,
          concurrency,
          async () => {
            const renewed = await firstValueFrom(this.fileService.createUploadSas(dto));
            return renewed.Url;
          },
          (percent) => this.updateProgress(item.id, percent),
          controller.signal
        );

        this.abortControllers.delete(item.id);
        this.abortAction.delete(item.id);

        const updateDto: ChatbotDocumentUpdateFileDTO = new ChatbotDocumentUpdateFileDTO();
        updateDto.IdClient = item.idClient;
        updateDto.IdProject = item.idProject;
        updateDto.IdChat = item.idChat;
        updateDto.Blobname = resp.Blobname;
        updateDto.Filename = file.name;
        updateDto.Filesize = file.size;
        updateDto.ContentType = file.type;
        updateDto.ContentHash = await this.computeFileContentHash(file);

        if(item.IsIa) {
          this.documentService.updateFileIa(item.idDocument, updateDto).subscribe();
        } else {
          this.documentService.updateFile(item.idDocument, updateDto).subscribe();
        }        
      } catch (err: unknown) {
        const msg = (err as Error)?.message;
        const isAborted = msg === FileService.ABORTED_ERROR || (err as { code?: string })?.code === FileService.ABORTED_ERROR;

        this.abortControllers.delete(item.id);
        const action = this.abortAction.get(item.id);
        this.abortAction.delete(item.id);

        if (isAborted && action === 'pause') {
          this.setItemPaused(item.id, file.name, file.size);
        } else if (isAborted && action === 'cancel') {
          this.removeItem(item.id);
        } else {
          this.setError(item.id);
        }
      }
    });
  }

  /**
   * Genera el hash SHA-256 del contenido del fichero (mismo criterio que el backend).
   * @param file Fichero del que calcular el hash
   * @returns Hash en hexadecimal en minúsculas
   */
  private async computeFileContentHash(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /** Marca el ítem como pausado (interrumpido): sin File, mismo progreso, reanudable después. */
  private setItemPaused(id: string, fileName: string, fileSize: number): void {
    const current = this.itemsSubject.value;
    const updated = current.map(it =>
      it.id === id
        ? { ...it, file: { name: fileName, size: fileSize }, error: false }
        : it
    );
    this.itemsSubject.next(updated);
    this.saveToStorage();
  }

  /** Pausa la subida en curso: la detiene y deja el ítem listo para reanudar más tarde. */
  pauseUpload(itemId: string): void {
    this.abortAction.set(itemId, 'pause');
    this.abortControllers.get(itemId)?.abort();
  }

  /** Detiene la subida en curso y quita el ítem de la lista. */
  cancelUpload(itemId: string): void {
    this.abortAction.set(itemId, 'cancel');
    this.abortControllers.get(itemId)?.abort();
  }  
}
